import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import Button3D from './Button3D';
import ProviderBtn from './ProviderBtn';
import apiClient from '../services/apiClient';
import { FaGoogle, FaMicrosoft, FaApple } from 'react-icons/fa';
import styles from './oauthStyles';
import { FEATURE_FLAGS } from '../constants';

const MemberInvitation = () => {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [inviteData, setInviteData] = useState(null);
  const [error, setError] = useState(null);
  
  // Form state
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [provider, setProvider] = useState('');
  const [appleId, setAppleId] = useState('');
  const [appPassword, setAppPassword] = useState('');
  const [message, setMessage] = useState('');
  const [authText, setAuthText] = useState('');
  const [isAuthorizing, setIsAuthorizing] = useState(false);
  const [authCompleted, setAuthCompleted] = useState(false);

  // Load invitation data
  useEffect(() => {
    const loadInvitation = async () => {
      try {
        console.log('🔍 Loading invitation for token:', token);
        
        const response = await apiClient.get(`/member-invitation/${token}`);
        if (response.data.success) {
          setInviteData(response.data.invite);
          setEmail(response.data.invite.memberEmail);
          console.log('✅ Invitation loaded:', response.data.invite);
        } else {
          setError(response.data.message);
        }
      } catch (err) {
        console.error('❌ Error loading invitation:', err);
        console.error('❌ Full error details:', {
          status: err.response?.status,
          statusText: err.response?.statusText,
          data: err.response?.data,
          url: err.config?.url,
          method: err.config?.method
        });
        
        if (err.response?.status === 404) {
          setError(err.response?.data.message || 'Invitation not found.');
        } else {
          setError(`Failed to load invitation. Error: ${err.response?.status || 'Network'} - ${err.response?.statusText || err.message}`);
        }
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      loadInvitation();
    } else {
      setError('Invalid invitation URL');
      setLoading(false);
    }
  }, [token]);

  // Handle provider selection
  const handleProvider = (selectedProvider) => {
    setProvider(selectedProvider);
    setMessage('');
    setAuthText('');
  };

  // Handle authorization (similar to existing auth process)
  const authorize = async () => {
    setMessage(null);

    if (!email) {
      setMessage("Please enter your email.");
      return;
    }

    if (!provider) {
      setMessage("Please select a calendar type.");
      return;
    }

    setIsAuthorizing(true);
    try {
      if (provider === "icloud") {
        if (!appleId || !appPassword) {
          setMessage("Please fill in your Apple ID and App-Specific Password.");
        } else {
          const res = await apiClient.post(
            '/auth/icloud',
            { email, phone, apple_id: appleId, app_password: appPassword }
          );

          if (res.data.connected) {
            // Complete the member invitation for iCloud
            await completeInvitation({
              email,
              phone,
              provider,
              appleId,
              appPassword
            }, true); // Pass true to indicate iCloud success
          } else {
            setMessage(res.data.message || "Invalid credentials.");
          }
        }
      } else {
        const res = await apiClient.post(
          '/auth/request',
          { email, phone, provider }
        );
        
        if (res.data.connected) {
          // Provider directly connected (no OAuth flow needed)
          await completeInvitation({
            email,
            phone,
            provider,
            connected: true
          }, false, res.data.message); // Pass backend message
        } else if (res.data.auth_text) {
          setAuthText(res.data.auth_text);
          // For OAuth providers, we'll complete after user follows the auth process
          // For now, we'll complete immediately for demo purposes
          await completeInvitation({
            email,
            phone,
            provider,
            authText: res.data.auth_text
          }, false, res.data.auth_text); // Pass auth_text as the success message
        } else {
          setMessage(res.data.error || res.data.message || "Failed to start authentication.");
        }
      }
    } catch (err) {
      setMessage(`Error: ${err.message}`);
    } finally {
      setIsAuthorizing(false);
    }
  };

  // Complete the invitation
  const completeInvitation = async (memberData, isICloud = false, backendMessage = null) => {
    try {
      console.log('✅ Completing invitation with data:', memberData);
      
      const response = await apiClient.post('/complete-member-invitation', {
        inviteToken: token,
        memberData
      });
      
      if (response.data.success) {
        setAuthCompleted(true);
        
        // Provider-specific success messages
        if (isICloud) {
          setMessage(
            <>
              ✅ iCloud connected!<br />
              You're all set! Just email your documents to{' '}
              <a href="mailto:agent@smartclose.app">
                agent@smartclose.app
              </a>
              , and we'll create tasks in your Apple Calendar & Reminders automatically.
            </>
          );
        } else if (backendMessage) {
          // Use the backend service's response message
          setMessage(
            <>
              ✅ {backendMessage}
            </>
          );
        } else {
          // Default message for other providers
          setMessage(
            <>
              ✅ Calendar connected successfully!<br />
              You're all set! Just email your documents to{' '}
              <a href="mailto:agent@smartclose.app">
                agent@smartclose.app
              </a>
              , and we'll create tasks in your calendar automatically.
            </>
          );
        }
      } else {
        setMessage(response.data.message || 'Failed to complete setup.');
      }
    } catch (err) {
      console.error('❌ Error completing invitation:', err);
      setMessage('Failed to complete setup. Please try again.');
    }
  };

  // Loading state
  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <div style={{ textAlign: 'center' }}>
          <h2>Loading invitation...</h2>
          <div style={{ marginTop: '20px', fontSize: '18px' }}>⏳</div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <div style={{ textAlign: 'center', maxWidth: '500px', padding: '40px' }}>
          <h2 style={{ color: '#f44336' }}>❌ Invitation Error</h2>
          <p style={{ fontSize: '16px', color: '#666', marginTop: '20px' }}>{error}</p>
          <p style={{ fontSize: '14px', color: '#999', marginTop: '20px' }}>
            Please contact your administrator for a new invitation link.
          </p>
        </div>
      </div>
    );
  }

  // Success state
  if (authCompleted) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <div style={{ background: '#fff', borderRadius: 8, padding: '40px', maxWidth: '600px', textAlign: 'center', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}>
          <h2 style={{ color: '#4CAF50', marginBottom: '20px' }}>
            {provider === 'icloud' ? '🎉 Setup Complete!' : 'Almost There!'}
          </h2>
          {message && <div style={styles.message}>{message}</div>}
          {provider === 'icloud' && (
            <p style={{ marginTop: '30px', fontSize: '14px', color: '#666' }}>
              Your calendar is connected to {inviteData?.companyName}'s SmartClose system.
            </p>
          )}
          
          {/* Close Button */}
          <div style={{ textAlign: 'center', marginTop: '30px' }}>
            <Button3D
              onClick={() => window.close()}
              style={{ 
                minWidth: '200px', 
                fontSize: '16px', 
                padding: '12px 24px'
              }}
            >
              Close
            </Button3D>
          </div>
        </div>
      </div>
    );
  }

  // Main invitation form
  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', background: '#f5f5f5' }}>
      <div style={{ background: '#fff', borderRadius: 8, padding: '40px', maxWidth: '600px', width: '100%', margin: '20px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}>
        
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '30px' }}>
          <h2 style={{ color: '#333', marginBottom: '10px' }}>📅 Calendar Setup</h2>
          <p style={{ color: '#666', fontSize: '16px' }}>
            <strong>{inviteData?.invitedBy}</strong> has invited you to connect your calendar to <strong>SmartClose</strong>
          </p>
        </div>

        {/* Email Field */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', fontWeight: '500', marginBottom: '8px' }}>Email Address:</label>
          <input
            type="email"
            placeholder="your@email.com"
            value={email}
            onChange={e => setEmail(e.target.value)}
            style={styles.input}
            disabled // Pre-filled from invitation
          />
        </div>

        {/* Phone Field - only if phone feature is enabled */}
        {FEATURE_FLAGS.PHONE_FEATURE_ENABLED && (
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', fontWeight: '500', marginBottom: '8px' }}>Phone Number:</label>
            <input
              type="tel"
              placeholder="(555) 123-4567"
              value={phone}
              onChange={e => setPhone(e.target.value)}
              style={styles.input}
            />
          </div>
        )}

        {/* Calendar Type Selection */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', fontWeight: '500', marginBottom: '12px' }}>Select Your Calendar Type:</label>
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', justifyContent: 'center' }}>
            <ProviderBtn
              icon={<FaGoogle color="#DB4437" />}
              label="Google"
              selected={provider === "google"}
              onClick={() => handleProvider("google")}
            />
            <ProviderBtn
              icon={<FaMicrosoft color="#0078D4" />}
              label="Outlook"
              selected={provider === "outlook"}
              onClick={() => handleProvider("outlook")}
            />
            <ProviderBtn
              icon={<FaApple color="#000" />}
              label="iCloud"
              selected={provider === "icloud"}
              onClick={() => handleProvider("icloud")}
            />
          </div>
        </div>

        {/* iCloud specific fields */}
        {provider === "icloud" && (
          <div style={styles.icloudSection}>
            <p style={styles.instructions}>
              1. Visit{' '}
              <a href="https://appleid.apple.com/account/manage" target="_blank" rel="noopener noreferrer">
                appleid.apple.com
              </a>{' '}
              → Security → App-Specific Passwords<br />
              2. Click "Generate Password", enter a label like "SmartClose", and click "Create"<br />
              3. Copy the 16-character code and enter below
            </p>
            <div style={{ marginBottom: '15px' }}>
              <input
                type="email"
                placeholder="Apple ID"
                value={appleId}
                onChange={e => setAppleId(e.target.value)}
                style={styles.input}
              />
            </div>
            <div style={{ marginBottom: '15px' }}>
              <input
                type="password"
                placeholder="App-Specific Password"
                value={appPassword}
                onChange={e => setAppPassword(e.target.value)}
                style={styles.input}
              />
            </div>
          </div>
        )}

        {/* Messages */}
        {message && <div style={styles.message}>{message}</div>}
        {authText && <textarea readOnly style={styles.output} value={authText} />}

        {/* Authorization Button */}
        <div style={{ textAlign: 'center', marginTop: '30px' }}>
          <Button3D
            onClick={authorize}
            disabled={isAuthorizing || !provider}
            style={{ 
              minWidth: '200px', 
              fontSize: '16px', 
              padding: '12px 24px',
              opacity: (isAuthorizing || !provider) ? 0.6 : 1,
              cursor: (isAuthorizing || !provider) ? 'not-allowed' : 'pointer'
            }}
          >
            {isAuthorizing ? 'Connecting...' : 'Connect Calendar'}
          </Button3D>
        </div>

        {/* Footer */}
        <div style={{ textAlign: 'center', marginTop: '30px', fontSize: '12px', color: '#999' }}>
          SmartClose Calendar Integration
        </div>
      </div>
    </div>
  );
};

export default MemberInvitation;
